import { Controller } from "@hotwired/stimulus"

// The /talent-flows node-link diagram, rendered by force-graph (canvas; loaded
// as the page-scoped force-graph-lib bundle — NOT in the main application
// bundle). Direction reads three ways without relying on brand palettes:
// animated particles travel source → destination along each (curved) edge, an
// arrowhead sits at the destination rim, and edges brighten toward the
// destination. Clicking a company opens a small modal with its inbound/outbound
// flows and a link to its live desk / company page. Data fetches are
// token-guarded and cached per range chip (company_typeahead pattern).
const HEIGHT = 640
const COBALT = "#3A45E6"
const INK = "#17170F"
// Focus-mode edge colors, from the focused company's point of view:
// inbound = people it hired (green), outbound = people it lost (rose).
const IN_COLOR = "#16A34A"
const OUT_COLOR = "#E11D48"

export default class extends Controller {
  static targets = ["canvas", "status", "chip", "modal", "loader", "fullscreen"]
  static values = { url: String, range: { type: String, default: "90d" } }

  connect() {
    this.token = 0
    this.cache = new Map()
    this.images = new Map() // logo url → HTMLImageElement
    this.reducedMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches
    this.onDocClick = (e) => { if (!this.modalTarget.contains(e.target)) this.closeModal() }
    this.onKeydown = (e) => {
      if (e.key !== "Escape") return
      if (this.fullscreen) this.toggleFullscreen()
      else this.closeModal()
    }
    this.whenLibReady(() => this.load())
  }

  disconnect() {
    clearTimeout(this.fitTimer)
    this.graph?._destructor?.()
    this.resizeObserver?.disconnect()
    document.removeEventListener("click", this.onDocClick, true)
    document.removeEventListener("keydown", this.onKeydown)
  }

  // The lib script is deferred and may land after Stimulus connects.
  whenLibReady(fn, tries = 0) {
    if (window.ForceGraph) return fn()
    if (tries > 200) return this.status("Couldn't load the graph renderer.")
    setTimeout(() => this.whenLibReady(fn, tries + 1), 25)
  }

  setRange(event) {
    const range = event.currentTarget.dataset.range
    if (!range || range === this.rangeValue) return
    this.rangeValue = range
    this.chipTargets.forEach((c) => c.setAttribute("aria-pressed", c.dataset.range === range ? "true" : "false"))
    this.closeModal()
    this.load()
  }

  async load() {
    const range = this.rangeValue
    if (this.cache.has(range)) return this.render(this.cache.get(range))
    const mine = ++this.token
    this.status("Loading moves…")
    try {
      const res = await fetch(`${this.urlValue}?range=${encodeURIComponent(range)}`, { headers: { Accept: "application/json" } })
      if (!res.ok || mine !== this.token) return
      const data = await res.json()
      this.cache.set(range, data)
      if (mine === this.token) this.render(data)
    } catch (_e) {
      if (mine === this.token) {
        this.status("Couldn't load the graph — try again in a moment.")
        this.reveal() // don't strand the spinner over the error state
      }
    }
  }

  // ── render ────────────────────────────────────────────────────────────────

  render(data) {
    this.payload = data
    this.closeModal()
    if (!data.nodes.length) {
      this.status("No attributable moves in this window yet.")
      this.graph?.graphData({ nodes: [], links: [] })
      this.element.classList.add("is-empty")
      this.reveal()
      return
    }
    this.element.classList.remove("is-empty")
    const total = data.edges.reduce((s, e) => s + e.count, 0)
    this.status(`Top ${data.nodes.length} companies by movement · ${total} moves · ${data.period.since} → ${data.period.until}`)

    const maxMoves = Math.max(...data.nodes.map((n) => n.moves), 1)
    const nodes = data.nodes.map((n) => ({ ...n, r: 8 + 10 * Math.sqrt(n.moves / maxMoves) }))
    const links = data.edges.map((e) => ({ ...e }))
    nodes.forEach((n) => this.preload(n.logo))

    this.buildGraph()
    this.graph.graphData({ nodes, links })
    // The loader covers the canvas until the layout settles; onEngineStop fits
    // the view and fades the finished graph in. The timer is the backstop —
    // engine-stop has proven unreliable across sessions, and a spinner that
    // never lifts is worse than a fit that lands mid-settle.
    this.needsFit = true
    clearTimeout(this.fitTimer)
    this.fitTimer = setTimeout(() => this.fitAndReveal(), 4500)
  }

  fitAndReveal() {
    if (!this.needsFit) return
    this.needsFit = false
    this.graph.zoomToFit(0, 60)
    this.reveal()
  }

  reveal() {
    if (this.hasLoaderTarget) this.loaderTarget.hidden = true
    this.canvasTarget.classList.add("tf-ready")
  }

  // ── fullscreen ───────────────────────────────────────────────────────────

  toggleFullscreen() {
    const frame = this.canvasTarget.closest(".tf-frame")
    this.fullscreen = frame.classList.toggle("tf-full")
    document.documentElement.classList.toggle("tf-noscroll", this.fullscreen)
    if (this.hasFullscreenTarget) {
      this.fullscreenTarget.innerHTML = this.fullscreen ? this.icon("close") : this.icon("expand")
      this.fullscreenTarget.setAttribute("aria-label", this.fullscreen ? "Exit full screen" : "Full screen")
    }
    // The ResizeObserver re-sizes the canvas; refit once the new box has settled.
    requestAnimationFrame(() => requestAnimationFrame(() => this.graph?.zoomToFit(0, 60)))
  }

  icon(kind) {
    const path = kind === "close"
      ? '<path d="M18 6 6 18"/><path d="m6 6 12 12"/>'
      : '<path d="M8 3H5a2 2 0 0 0-2 2v3"/><path d="M21 8V5a2 2 0 0 0-2-2h-3"/><path d="M3 16v3a2 2 0 0 0 2 2h3"/><path d="M16 21h3a2 2 0 0 0 2-2v-3"/>'
    return `<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${path}</svg>`
  }

  buildGraph() {
    if (this.graph) return
    const container = this.canvasTarget
    // force-graph ≥1.49 is class-based: `new ForceGraph(el)`, chained setters unchanged.
    const graph = new window.ForceGraph(container)
      .width(container.clientWidth)
      .height(container.clientHeight || HEIGHT)
      .backgroundColor("#F7F7F3")
      .nodeId("id")
      .nodeVal((n) => n.r * n.r * 0.16) // collision/interaction area tracks the drawn radius
      .nodeLabel(() => null)
      .nodeCanvasObject((n, ctx, scale) => this.drawNode(n, ctx, scale))
      .nodePointerAreaPaint((n, color, ctx) => {
        ctx.fillStyle = color
        ctx.beginPath()
        ctx.arc(n.x, n.y, n.r + 4, 0, 2 * Math.PI)
        ctx.fill()
      })
      .linkCurvature(0.22)
      // Focus mode strips the noise completely: only the focused company's edges
      // (lines AND particles) stay visible, so what's moving is unambiguous.
      .linkVisibility((l) => !this.focus || this.linkTouchesFocus(l))
      // Lines stay LIGHT in focus mode so the solid-colored dots read clearly.
      .linkColor((l) => {
        const dir = this.focusDir(l)
        return dir ? this.hexWithAlpha(dir === "in" ? IN_COLOR : OUT_COLOR, 0.22) : "rgba(23,23,15,0.07)"
      })
      .linkWidth((l) => (this.linkTouchesFocus(l) ? 1.4 : 0.6) + Math.log2(l.count))
      .onNodeClick((n) => { this.setFocus(n); this.openModal(n) })
      .onNodeHover((n) => { if (!this.pinnedFocus) this.setFocus(n, false) })
      .onBackgroundClick(() => this.closeModal())
      .onNodeDrag(() => this.closeModal())
      .onEngineStop(() => this.fitAndReveal())

    graph.warmupTicks(160).cooldownTime(4000)
    if (this.reducedMotion) {
      // No particles without motion — arrowheads carry direction instead.
      graph.cooldownTicks(0).warmupTicks(240)
        .linkDirectionalArrowLength(4.5).linkDirectionalArrowRelPos(0.94)
        .linkDirectionalArrowColor(() => "rgba(23,23,15,0.35)")
    } else {
      // The direction cue: particles travel source → destination.
      graph.linkDirectionalParticles((l) => Math.min(4, 1 + Math.floor(l.count / 3)))
        .linkDirectionalParticleSpeed(0.00245)
        .linkDirectionalParticleWidth(2.4)
        .linkDirectionalParticleColor((l) => {
          const dir = this.focusDir(l)
          return dir ? (dir === "in" ? IN_COLOR : OUT_COLOR) : (this.accentOf(l.source) || COBALT)
        })
    }

    // Spread the layout: stronger repulsion + longer links, and a per-node
    // collision radius (node + label breathing room) so the core doesn't clump.
    graph.d3Force("charge")?.strength(-320)
    graph.d3Force("link")?.distance(60)
    if (window.ForceGraphCollide) graph.d3Force("collide", window.ForceGraphCollide((n) => n.r + 14))

    this.graph = graph
    this.resizeObserver = new ResizeObserver(() =>
      graph.width(container.clientWidth).height(container.clientHeight))
    this.resizeObserver.observe(container)
    document.addEventListener("click", this.onDocClick, true)
    document.addEventListener("keydown", this.onKeydown)
  }

  // Focus = the hovered (transient) or clicked (pinned while the modal is open)
  // company: its edges light up in its accent, everything unrelated fades back.
  setFocus(node, pin = true) {
    this.focus = node || null
    this.pinnedFocus = pin && !!node
    this.neighbors = new Set()
    if (node && this.payload) {
      this.payload.edges.forEach((e) => {
        const s = typeof e.source === "object" ? e.source.id : e.source
        const t = typeof e.target === "object" ? e.target.id : e.target
        if (s === node.id) this.neighbors.add(t)
        if (t === node.id) this.neighbors.add(s)
      })
    }
    // Accessors are sampled when set — re-poke them so visibility (and its
    // particles) re-evaluate against the new focus immediately.
    if (this.graph) this.graph.linkVisibility(this.graph.linkVisibility())
  }

  linkTouchesFocus(l) {
    return this.focusDir(l) !== null
  }

  // "in" (edge lands on the focused node), "out" (leaves it), or null.
  focusDir(l) {
    if (!this.focus) return null
    const s = typeof l.source === "object" ? l.source.id : l.source
    const t = typeof l.target === "object" ? l.target.id : l.target
    if (t === this.focus.id) return "in"
    if (s === this.focus.id) return "out"
    return null
  }

  drawNode(n, ctx, scale) {
    const accent = n.accent || COBALT
    const dimmed = this.focus && n.id !== this.focus.id && !this.neighbors.has(n.id)
    if (dimmed) ctx.globalAlpha = 0.16
    ctx.beginPath()
    ctx.arc(n.x, n.y, n.r, 0, 2 * Math.PI)
    ctx.fillStyle = "#fff"
    ctx.fill()
    ctx.lineWidth = 1.2
    ctx.strokeStyle = accent
    ctx.stroke()

    const img = n.logo && this.images.get(n.logo)
    if (img?.complete && img.naturalWidth > 0) {
      ctx.save()
      ctx.beginPath()
      ctx.arc(n.x, n.y, n.r - 1.6, 0, 2 * Math.PI)
      ctx.clip()
      const d = (n.r - 1.6) * 2
      ctx.drawImage(img, n.x - d / 2, n.y - d / 2, d, d)
      ctx.restore()
    } else {
      ctx.beginPath()
      ctx.arc(n.x, n.y, n.r - 1.6, 0, 2 * Math.PI)
      ctx.fillStyle = this.hexWithAlpha(accent, 0.12)
      ctx.fill()
      ctx.font = `700 ${Math.max(7, n.r * 0.85)}px "Hanken Grotesk", sans-serif`
      ctx.textAlign = "center"
      ctx.textBaseline = "middle"
      ctx.fillStyle = accent
      ctx.fillText(n.monogram, n.x, n.y + 0.5)
    }

    // Label: constant screen size; hide when zoomed far out to fight clutter.
    if (scale > 0.55) {
      const size = 10.5 / scale
      ctx.font = `500 ${size}px "IBM Plex Mono", monospace`
      ctx.textAlign = "center"
      ctx.textBaseline = "top"
      const label = n.name.length > 24 ? `${n.name.slice(0, 23)}…` : n.name
      ctx.fillStyle = "rgba(255,255,255,0.82)"
      const w = ctx.measureText(label).width
      ctx.fillRect(n.x - w / 2 - 2 / scale, n.y + n.r + 2.5 / scale, w + 4 / scale, size + 3 / scale)
      ctx.fillStyle = "rgba(23,23,15,0.62)"
      ctx.fillText(label, n.x, n.y + n.r + 4 / scale)
    }
    if (dimmed) ctx.globalAlpha = 1
  }

  // ── modal ────────────────────────────────────────────────────────────────

  openModal(node) {
    const flows = { inbound: [], outbound: [] }
    this.payload.edges.forEach((e) => {
      if (e.target === node.id) flows.inbound.push({ name: this.nameOf(e.source), count: e.count })
      if (e.source === node.id) flows.outbound.push({ name: this.nameOf(e.target), count: e.count })
    })
    flows.inbound.sort((a, b) => b.count - a.count)
    flows.outbound.sort((a, b) => b.count - a.count)

    const esc = (s) => String(s ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]))
    const list = (rows, dir) => rows.length
      ? rows.slice(0, 6).map((r) => `<li><span>${dir === "in" ? `${esc(r.name)} →` : `→ ${esc(r.name)}`}</span><strong>${r.count}</strong></li>`).join("")
      : `<li class="tfm-none">None in this window</li>`

    const link = node.live_url
      ? `<a href="${esc(node.live_url)}">Open the ${esc(node.name)} live desk →</a>`
      : (node.url ? `<a href="${esc(node.url)}">Company page →</a>` : "")

    this.modalTarget.innerHTML = `
      <button type="button" class="tfm-close" data-action="talent-flow#closeModal" aria-label="Close">×</button>
      <div class="tfm-head">
        ${node.logo ? `<img src="${esc(node.logo)}" alt="" onerror="this.remove()">` : ""}
        <div><strong>${esc(node.name)}</strong><span>${node.moves} ${node.moves === 1 ? "move" : "moves"} in this window</span></div>
      </div>
      <div class="tfm-cols">
        <div><h4><span class="tfm-dot" style="background:${IN_COLOR}"></span>Hiring from</h4><ul>${list(flows.inbound, "in")}</ul></div>
        <div><h4><span class="tfm-dot" style="background:${OUT_COLOR}"></span>Losing to</h4><ul>${list(flows.outbound, "out")}</ul></div>
      </div>
      ${link ? `<div class="tfm-link">${link}</div>` : ""}`
    this.modalTarget.hidden = false
  }

  closeModal() {
    if (this.hasModalTarget) this.modalTarget.hidden = true
    this.setFocus(null)
  }

  // ── helpers ──────────────────────────────────────────────────────────────

  preload(url) {
    if (!url || this.images.has(url)) return
    // NO crossOrigin: the logo CDN sends no CORS headers, so an anonymous request
    // fails outright. The canvas gets tainted, which is fine — we never read pixels.
    const img = new Image()
    // The engine may have stopped redrawing by the time the logo arrives — poke
    // any no-op setter to schedule a re-render so late logos still paint.
    img.onload = () => this.graph?.nodeRelSize(this.graph.nodeRelSize())
    img.src = url
    this.images.set(url, img)
  }

  // After graphData(), link source/target become node objects; tolerate both.
  accentOf(end) { return (typeof end === "object" ? end : this.payload.nodes.find((n) => n.id === end))?.accent }

  nameOf(end) {
    const id = typeof end === "object" ? end.id : end
    return this.payload.nodes.find((n) => n.id === id)?.name || "Unknown"
  }

  hexWithAlpha(hex, alpha) {
    const m = /^#?([0-9a-f]{6})$/i.exec(hex)
    if (!m) return `rgba(58,69,230,${alpha})`
    const v = parseInt(m[1], 16)
    return `rgba(${(v >> 16) & 255},${(v >> 8) & 255},${v & 255},${alpha})`
  }

  status(msg) {
    if (this.hasStatusTarget) this.statusTarget.textContent = msg
  }
}
